import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Megaphone, Package } from 'lucide-react';

import { CountPill } from '@/components/admin/CorpBadges';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableAction, DataTableColumn } from '@/components/ui/DataTable';
import { usePatientNavigation } from '@/components/ui/PatientLink';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { usePageContext } from '@/hooks/usePageContext';
import { PackageDetailModal as SharedPackageDetailModal } from '@/pages/patients/PackageDetailModal';
import {
  getPackageManagementRows,
  type PackageManagementFilters,
  type PackageManagementRow,
} from '@/services/packages-management.service';
import { usePackageFilterOptions } from '@/hooks/filters';
import { buildApiParams, buildMarketingFilterConditions, extractSearch } from '@/lib/filterRegistry';
import { MarketingActionsModal } from '@/components/marketing/MarketingActionsModal';
import { useAuth } from '@/hooks/useAuth';
import {
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';

import {
  EMPTY_PACKAGE_STATS,
  formatPackageCurrency,
  formatPackageDate,
  PACKAGE_FILTER_CONFIGS,
  packageStatusLabel,
  PACKAGE_STATUS_VARIANTS,
} from './packagesManagementShared';

export function PackageManagementList() {
  const navToPatient = usePatientNavigation();
  const { hasPermission } = useAuth();
  const updateAIContext = usePageContext('All Packages');

  const filterState = useLocalDashboardFilters(PACKAGE_FILTER_CONFIGS, 'packages');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [detailRow, setDetailRow] = useState<PackageManagementRow | null>(null);
  const [marketingModalOpen, setMarketingModalOpen] = useState(false);
  const { exporting, startExport, exportModal } = useAsyncExport({ entityType: 'packages', entityLabel: 'packages' });

  const baseFilters = useMemo(
    () => ({ page, perPage, sortBy, sortDir }),
    [page, perPage, sortBy, sortDir],
  );

  const effectiveFilters = useMemo(
    () => buildApiParams(effectiveValues, filterState.negations, baseFilters),
    [baseFilters, effectiveValues, filterState.negations],
  );

  const search = extractSearch(effectiveValues);
  const activeFilters = useMemo(
    () =>
      Object.entries(effectiveValues)
        .filter(([key, val]) => {
          if (key === 'search') return false;
          if (Array.isArray(val)) return val.length > 0;
          if (typeof val === 'object' && val !== null && 'from' in val) return !!(val.from || val.to);
          return false;
        })
        .map(([key]) => key)
        .join(', '),
    [effectiveValues],
  );

  const marketingFilterConditions = useMemo(
    () => buildMarketingFilterConditions('package', effectiveValues, filterState.negations),
    [effectiveValues, filterState.negations],
  );

  useEffect(() => {
    setPage(1);
  }, [effectiveValues]);

  const sidebarOptions = usePackageFilterOptions();

  const listQuery = useQuery({
    queryKey: ['package-management', effectiveFilters],
    queryFn: () => getPackageManagementRows(effectiveFilters as PackageManagementFilters),
  });

  const result = listQuery.data;
  const stats = result?.stats ?? EMPTY_PACKAGE_STATS;

  useEffect(() => {
    const filterParts: string[] = [];
    if (activeFilters) filterParts.push(activeFilters);
    if (search) filterParts.push(`Search: "${search}"`);

    updateAIContext({
      filters: filterParts.length > 0 ? filterParts.join(', ') : 'No filters applied',
      dataSummary: `${result?.pagination.total ?? 0} packages (${stats.activeCount} active, ${stats.declinedCount} declined, ${stats.inactiveCount} inactive)`,
    });
  }, [activeFilters, search, result?.pagination.total, stats.activeCount, stats.declinedCount, stats.inactiveCount, updateAIContext]);


  const renderDate = (value: string | null | undefined) => {
    const d = formatPackageDate(value);
    if (!d) return <span className="text-xs text-slate-400">{'\u2014'}</span>;
    return (
      <div className="leading-tight">
        <p className="text-xs text-slate-700">{d.monthDay}</p>
        <p className="text-[11px] text-slate-400">{d.year}</p>
      </div>
    );
  };

  const columns = useMemo<DataTableColumn<PackageManagementRow>[]>(() => [
    {
      key: 'createdAt',
      header: 'Date',
      sortable: true,
      render: (row) => renderDate(row.createdAt),
    },
    {
      key: 'patientName',
      header: 'Patient',
      sortable: true,
      width: '220px',
      render: (row) => <span className="font-medium text-slate-800">{row.patientName}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (row) => <span className="text-xs text-slate-700">{row.type || '\u2014'}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (row) => <span className="text-xs font-medium text-slate-800">{formatPackageCurrency(row.amount)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => (
        <Badge variant={PACKAGE_STATUS_VARIANTS[row.status] ?? 'neutral'} dot>
          {packageStatusLabel(row.status)}
        </Badge>
      ),
    },
    {
      // The Product Types admin's Products badge; blank when nothing is left.
      key: 'productsRemaining',
      header: 'Remaining',
      sortable: true,
      render: (row) => (
        <CountPill tone="blue" icon={<Package />} count={row.productsRemaining ?? 0} title="Products remaining" />
      ),
    },
    {
      key: 'notes',
      header: 'Notes',
      sortable: true,
      render: (row) => <span className="text-xs text-slate-600">{row.notes || '\u2014'}</span>,
    },
  ], []);

  const actions = useMemo<DataTableAction<PackageManagementRow>[]>(() => [
    {
      label: 'View Details',
      onClick: (row) => setDetailRow(row),
    },
    {
      label: 'View Patient',
      onClick: (row) => navToPatient({ patientId: row.patientId, name: row.patientName }),
    },
  ], [navToPatient]);

  function handleExport() {
    startExport(effectiveFilters as unknown as Record<string, unknown>);
  }

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={PACKAGE_FILTER_CONFIGS}
            values={effectiveValues}
            options={sidebarOptions}
            negations={filterState.negations}
            onChange={filterState.setFilter}
            onNegateChange={filterState.setNegation}
            onClear={filterState.clearFilter}
            searchCommitMode="blur-or-enter"
            showAIAssistant={false}
            storageKey="packages"
            lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              variant="compact"
              title="All Packages"
              actions={(
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Download className="h-4 w-4" />}
                    disabled={(result?.pagination.total ?? 0) === 0 || exporting}
                    loading={exporting}
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                  {exportModal}
                  {hasPermission(20) && (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Megaphone className="h-4 w-4" />}
                      onClick={() => setMarketingModalOpen(true)}
                    >
                      Email / SMS List
                    </Button>
                  )}
                </>
              )}
            />
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<PackageManagementRow>
              data={result?.items ?? []}
              columns={columns}
              keyField="patientPackageId"
              loading={listQuery.isLoading}
              rowActions={actions}
              onRowClick={(row) => setDetailRow(row)}
              page={result?.pagination.page ?? page}
              pageSize={result?.pagination.perPage ?? perPage}
              total={result?.pagination.total ?? 0}
              onPageChange={setPage}
              manualSort
              sortKey={result?.sort.field ?? sortBy}
              sortDir={result?.sort.direction ?? sortDir}
              onSortChange={(key, direction) => {
                setSortBy(key);
                setSortDir(direction);
                setPage(1);
              }}
              emptyTitle="No packages found"
              emptyDescription="Try adjusting the management filters."
            />
          </div>
        </div>
      </ListPageLayout>

      {detailRow && (
        <SharedPackageDetailModal
          open={!!detailRow}
          onClose={() => setDetailRow(null)}
          patientId={detailRow.patientId}
          patientPackageId={detailRow.patientPackageId}
          patientName={detailRow.patientName}
          readonly
        />
      )}

      {hasPermission(20) && (
        <MarketingActionsModal
          isOpen={marketingModalOpen}
          onClose={() => setMarketingModalOpen(false)}
          sourceType="package"
          filterConditions={marketingFilterConditions}
        />
      )}
    </>
  );
}
